import { useState, useEffect } from "react";
import { useAuth } from "../auth/AuthProvider";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Sidebar from "../components/Sidebar";
import { useUserProfile } from "../hooks/useUserProfile";

const API_URL = process.env.REACT_APP_API_URL;

function UserSettings() {
  const auth = useAuth();
  const { profile: rdsProfile } = useUserProfile();
  const { t, i18n } = useTranslation();
  const [userInfo, setUserInfo] = useState({
    first_name: "",
    last_name: "",
    middle_initial: "",
    email: "",
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (rdsProfile === null) return;
    const oidc = auth.user?.profile || {};
    // Best-effort seed from OIDC name if RDS profile is empty
    const oidcName = oidc.name || "";
    const parts = oidcName.trim().split(" ");
    setUserInfo({
      first_name:     rdsProfile.first_name     || (parts.length >= 1 ? parts[0] : ""),
      last_name:      rdsProfile.last_name      || (parts.length >= 2 ? parts[parts.length - 1] : ""),
      middle_initial: rdsProfile.middle_initial || "",
      email:          oidc.email                || "",  // always from OIDC — not stored in RDS
    });
    setLoading(false);
  }, [rdsProfile, auth.user]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setUserInfo((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = auth.user?.access_token;
    if (!token) return;

    if (!userInfo.first_name.trim()) {
      Swal.fire({ icon: "error", title: t("required"), text: t("first_name_required") });
      return;
    }
    if (userInfo.middle_initial && !/^[a-zA-Z]$/.test(userInfo.middle_initial)) {
      Swal.fire({ icon: "error", title: t("invalid"), text: t("invalid_middle_initial") });
      return;
    }

    try {
      const res = await fetch(`${API_URL}/user/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          first_name:     userInfo.first_name.trim(),
          last_name:      userInfo.last_name.trim(),
          middle_initial: userInfo.middle_initial.trim().toUpperCase() || "",
        }),
      });
      if (!res.ok) throw new Error(`${res.status}`);
      Swal.fire({ icon: "success", title: t("saved_title"), text: t("profile_updated") });
    } catch (err) {
      Swal.fire({ icon: "error", title: t("save_failed"), text: t("could_not_update_profile", { error: err.message }) });
    }
  };

  const handleLanguageChange = async (lang) => {
    i18n.changeLanguage(lang);
    const token = auth.user?.access_token;
    if (!token) return;
    try {
      await fetch(`${API_URL}/user/profile`, {
        method: "PUT",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ language: lang }),
      });
    } catch (err) {
      console.error("Failed to save language preference:", err);
    }
  };

  const handleSignOut = () => {
    auth.signOut();
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-gray-100">
        <Sidebar handleSignOut={handleSignOut}>
          <div className="flex flex-1 items-center justify-center">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin h-12 w-12 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              <p className="text-gray-400 text-sm">Loading your settings…</p>
            </div>
          </div>
        </Sidebar>
      </div>
    );
  }

  const inputClass = "w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-green-400 text-sm";

  return (
    <div className="flex min-h-screen bg-gray-100">
      <Sidebar handleSignOut={handleSignOut}>
      <div className="flex-1 flex items-center justify-center p-3 sm:p-4">
        <div className="max-w-md w-full p-4 sm:p-6 bg-white shadow-md rounded-lg">
          <h1 className="text-2xl font-semibold text-gray-700 mb-1">{t("user_settings_title")}</h1>
          <p className="text-sm text-gray-400 mb-5">{t("user_settings_subtitle")}</p>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email — read-only */}
            <div>
              <label className="block mb-1 text-sm text-gray-600 font-medium">{t("email")}</label>
              <input
                type="email"
                value={userInfo.email}
                disabled
                className="w-full p-2 border border-gray-200 rounded bg-gray-50 cursor-not-allowed text-gray-400 text-sm"
              />
            </div>

            {/* First Name */}
            <div>
              <label className="block mb-1 text-sm text-gray-600 font-medium">{t("first_name")}</label>
              <input
                type="text"
                name="first_name"
                value={userInfo.first_name}
                onChange={handleChange}
                className={inputClass}
              />
            </div>

            {/* Middle Initial */}
            <div>
              <label className="block mb-1 text-sm text-gray-600 font-medium">{t("middle_initial")}</label>
              <input
                type="text"
                name="middle_initial"
                value={userInfo.middle_initial}
                onChange={handleChange}
                maxLength={1}
                className={`${inputClass} w-16`}
                placeholder="A"
              />
            </div>

            {/* Last Name */}
            <div>
              <label className="block mb-1 text-sm text-gray-600 font-medium">{t("last_name")}</label>
              <input
                type="text"
                name="last_name"
                value={userInfo.last_name}
                onChange={handleChange}
                className={inputClass}
              />
              <p className="mt-1 text-xs text-gray-400">{t("profile_synced")}</p>
            </div>

            <button
              type="submit"
              className="w-full bg-green-500 text-white py-2 rounded-lg hover:bg-green-600 transition text-sm font-medium"
            >
              {t("save_changes")}
            </button>
          </form>

          <div className="mt-6 pt-4 border-t border-gray-200">
            <label className="block mb-2 text-sm text-gray-600 font-medium">{t("language_label")}</label>
            <div className="flex gap-2">
              <button
                onClick={() => handleLanguageChange("en")}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  i18n.language === "en"
                    ? "bg-green-500 text-white border-green-500"
                    : "bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-600"
                }`}
              >
                {t("lang_english")}
              </button>
              <button
                onClick={() => handleLanguageChange("es")}
                className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                  i18n.language === "es"
                    ? "bg-green-500 text-white border-green-500"
                    : "bg-white text-gray-700 border-gray-300 hover:border-green-400 hover:text-green-600"
                }`}
              >
                {t("lang_spanish")}
              </button>
            </div>
          </div>

          <Link to="/" className="block text-center text-blue-500 hover:underline mt-4 text-sm">
            {t("back_to_dashboard")}
          </Link>
        </div>
      </div>
      </Sidebar>
    </div>
  );
}

export default UserSettings;
